import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { StyledTableCell, StyledTableHead, StyledTableRow } from './Style';
import { Column } from './Types';

type Props = {
  columns: Column[];
  rows: any[];
  tableHeight?: string;
  onRowDoubleClick?: (row: any) => void;
};

const dateRegex = /[0-9]{2}\/[0-9]{2}\/[0-9]{4}/

const dateFromString = (date: string) => {
  const [day, month, year] = date.split('/').map(Number);
  return new Date(year, month - 1, day);
}

const getRowValue = (row: any) => {
  if (row?.match?.(dateRegex)) return dateFromString(row)
  if (typeof row === 'string' || row instanceof String || typeof row === 'number' || !row) return row || ''
  return row?.props?.text || undefined
}

export default function ComponentTable({ columns, rows, tableHeight, onRowDoubleClick }: Props) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  const [orderBy, setOrderBy] = React.useState<string | null>(null);
  const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrderDirection('asc');
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!orderBy) return rows;

    return [...rows].sort((a, b) => {
      const aValue = getRowValue(a[orderBy]);
      const bValue = getRowValue(b[orderBy]);

      if (aValue < bValue) return orderDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return orderDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, orderBy, orderDirection]);


  // @ts-ignore - .
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Paper className={'mb-9'} sx={{ width: '100%', height: 'auto !important', overflow: 'hidden' }}>
      <TableContainer
        className={'custom-scrollbar'}
        sx={{ height: tableHeight ?? '60vh', overflow: 'auto' }}
      >
        <Table stickyHeader aria-label='sticky table'>
          <StyledTableHead sx={{ borderCollapse: 'collapse' }}>
            <StyledTableRow>
              {columns?.map((column: any) => (
                <StyledTableCell
                  key={column.id}
                  align={column.align}
                  onClick={() => handleSort(column.id)}
                  title={column.label}
                  style={{
                    width: column.width, minWidth: column.minWidth,
                    maxWidth: column.maxWidth || '150px', 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer', userSelect: 'none',
                    display: column.hidden ? 'none' : 'table-cell'
                  }}
                >
                  {column.label}
                  {orderBy === column.id && (
                    <span style={{ marginLeft: 6 }}>
                      {orderDirection === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          </StyledTableHead>
          <TableBody>
            {sortedRows
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <StyledTableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={index}
                  >                    
                    {columns.map((column) => {
                      const cellValue = row[column.id as keyof typeof row];
                      return (
                        <StyledTableCell
                          // @ts-ignore - .
                          key={column.id}
                          align={column.align}
                          padding='none'
                          title={typeof cellValue === 'string' || typeof cellValue === 'number' ? String(cellValue) : undefined}
                          onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
                          style={{
                            cursor: onRowDoubleClick ? 'pointer' : 'default',
                            display: column.hidden ? 'none' : 'table-cell',
                            maxWidth: column.maxWidth || column.width || '150px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {/*// @ts-ignore - .*/}
                          {row[column.id]}
                        </StyledTableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[100, 500, 1000]}
        component='div'
        count={rows?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
